import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, UserRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiError } from "@/lib/api";
import { getConsent } from "@/lib/consent";

const newSid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()).replace(/-/g, "").slice(0, 32);
const WELCOME = { role: "assistant", content: "Bonjour ! Je suis l'assistant virtuel (intelligence artificielle) de la Conciergerie Familly 29. Posez-moi vos questions sur nos prestations, formules ou l'espace client. Vous pouvez à tout moment demander à parler à un conseiller. Ne partagez pas de codes d'accès ni de données bancaires." };

const Handoff = ({ messages, onDone }) => {
  const [f, setF] = useState({ nom: "", email: "", telephone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const transcript = messages.slice(1).map((m) => `${m.role === "user" ? "Visiteur" : "Assistant"} : ${m.content}`).join("\n");
    try {
      await api.post("/public/demandes", { type: "contact", prestation: "Assistant en ligne – mise en relation", nom: f.nom, email: f.email, telephone: f.telephone,
        message: `${f.message}\n\n--- Échange avec l'assistant ---\n${transcript}`.slice(0, 2900), consentement: true });
      toast.success("Votre demande est transmise : la conciergerie vous recontacte rapidement.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const inp = "w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2840]";
  return (
    <form onSubmit={submit} className="space-y-2 p-3" data-testid="chat-handoff-form">
      <p className="text-xs text-slate-600">Laissez vos coordonnées, un conseiller vous répond directement.</p>
      <input aria-label="Nom" minLength={2} maxLength={120} required placeholder="Nom" className={inp} value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} data-testid="chat-handoff-nom" />
      <input aria-label="Email" required type="email" placeholder="Email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="chat-handoff-email" />
      <input aria-label="Téléphone" type="tel" maxLength={30} placeholder="Téléphone" className={inp} value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} data-testid="chat-handoff-tel" />
      <textarea aria-label="Votre demande" maxLength={2000} required rows={2} placeholder="Votre demande" className={inp} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="chat-handoff-message" />
      <p className="text-[10px] text-slate-500">En envoyant, vous acceptez le traitement de vos données pour répondre à votre demande.</p>
      <button disabled={busy} className="w-full rounded-full bg-[#D82C2C] py-2 text-sm font-bold text-white disabled:opacity-60" data-testid="chat-handoff-submit">Envoyer au conseiller</button>
    </form>
  );
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [cookieDialog, setCookieDialog] = useState(() => !getConsent());
  useEffect(() => {
    const onDialog = (event) => setCookieDialog(event.detail);
    window.addEventListener("f29-cookie-dialog", onDialog);
    return () => window.removeEventListener("f29-cookie-dialog", onDialog);
  }, []);
  const [handoff, setHandoff] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(newSid());
  const list = useRef(null);
  const scroll = () => setTimeout(() => list.current?.scrollTo(0, list.current.scrollHeight), 0);

  const active = useRef(null);
  const [retryText, setRetryText] = useState("");
  useEffect(() => () => active.current?.abort("closed"), []);
  const stop = () => {
    active.current?.abort("closed");
    active.current = null;
    setBusy(false);
    setMessages((m) => m.map((item) => item.role === "assistant" && !item.content ? { ...item, content: "Réponse interrompue. Vous pouvez poser une nouvelle question." } : item));
  };

  const sendText = async (text, retry = false) => {
    if (!text || active.current) return;
    const controller = new AbortController();
    active.current = controller;
    const timer = setTimeout(() => controller.abort("timeout"), 50000);
    setInput("");
    setRetryText("");
    setBusy(true);
    setMessages((m) => [...(retry ? m.slice(0, -1) : [...m, { role: "user", content: text }]), { role: "assistant", content: "" }]);
    scroll();
    let reader;
    try {
      const res = await fetch(`${API}/public/chat`, { method: "POST", signal: controller.signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid.current, message: text }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Service indisponible. Réessayez ou contactez un conseiller.");
      reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let complete = false;
      while (!complete) {
        const { value, done } = await reader.read();
        if (done) throw new Error("La réponse a été interrompue. Vous pouvez réessayer.");
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data:")) continue;
          const data = ev.slice(5).trim();
          if (data === "[DONE]") { complete = true; break; }
          const payload = JSON.parse(data);
          if (payload.error) throw new Error(payload.error);
          if (active.current !== controller) return;
          setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + (payload.t || "") }]);
          scroll();
        }
      }
    } catch (err) {
      if (active.current === controller) {
        const message = controller.signal.reason === "timeout" ? "La réponse prend trop de temps. Réessayez ou contactez un conseiller." : err.message;
        setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: message }]);
        setRetryText(text);
      }
    } finally {
      clearTimeout(timer);
      controller.abort();
      reader?.cancel().catch(() => {});
      if (active.current === controller) { active.current = null; setBusy(false); }
    }
  };
  const send = (e) => { e.preventDefault(); sendText(input.trim()); };

  if (cookieDialog) return null;
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="chat-open-btn" aria-label="Assistant en ligne"
        className="fixed bottom-20 left-5 md:bottom-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#0F2840] px-5 py-3 text-sm font-bold text-white shadow-xl transition-transform duration-200 hover:-translate-y-0.5">
        <MessageCircle className="h-4 w-4" /><span className="hidden min-[370px]:inline">Une question ?</span>
      </button>
    );
  }
  return (
    <div className="fixed bottom-20 left-5 md:bottom-5 z-50 flex h-[520px] max-h-[80vh] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl" data-testid="chat-widget">
      <div className="flex items-center justify-between bg-[#0F2840] px-4 py-3 text-white">
        <div className="flex items-center gap-2"><img src="/logo-sm.png" alt="" className="h-8 w-8 rounded-full bg-white" /><div><p className="text-sm font-bold">Assistant Familly 29</p><p className="text-[10px] text-white/70">Réponses automatiques · IA</p></div></div>
        <button onClick={() => { stop(); setOpen(false); }} aria-label="Fermer" data-testid="chat-close-btn"><X className="h-5 w-5" /></button>
      </div>
      {handoff ? <div className="flex-1 overflow-y-auto"><Handoff messages={messages} onDone={() => { setHandoff(false); setMessages((m) => [...m, { role: "assistant", content: "Merci ! Votre demande a été enregistrée par la conciergerie, qui vous recontactera." }]); scroll(); }} /></div> : (
        <div ref={list} className="flex-1 space-y-2 overflow-y-auto bg-slate-50 p-3" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`max-w-[85%] whitespace-pre-line rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "ml-auto bg-[#0F2840] text-white" : "bg-white text-slate-800 shadow-sm"}`} data-testid={`chat-msg-${m.role}-${i}`}>
              {m.content || <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            </div>
          ))}
        </div>
      )}
      {!handoff && retryText && <button type="button" onClick={() => sendText(retryText, true)} disabled={busy} className="border-t py-2 text-sm font-bold text-[#D82C2C]" data-testid="chat-retry-btn">Réessayer la réponse</button>}
      <button onClick={() => { stop(); setHandoff((h) => !h); }} data-testid="chat-handoff-btn" className="flex items-center justify-center gap-2 border-t border-slate-100 py-2 text-xs font-bold text-[#D82C2C] hover:bg-red-50">
        <UserRound className="h-3.5 w-3.5" />{handoff ? "Revenir à l'assistant" : "Parler à un conseiller"}
      </button>
      {!handoff && (
        <form onSubmit={send} className="flex gap-2 border-t border-slate-100 p-2">
          <input aria-label="Votre question" value={input} onChange={(e) => setInput(e.target.value)} maxLength={600} placeholder="Votre question…" className="flex-1 rounded-full border border-slate-200 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2840]" data-testid="chat-input" />
          <button disabled={busy} className="grid h-10 w-10 place-items-center rounded-full bg-[#D82C2C] text-white disabled:opacity-60" data-testid="chat-send-btn" aria-label="Envoyer"><Send className="h-4 w-4" /></button>
        </form>
      )}
    </div>
  );
}
